import { AlertCircleIcon, BellIcon, ChevronDownIcon } from '@orn/shared-web/icons';
import { cx } from '@orn/shared-web/utils/cx';
import * as React from 'react';

import AlertBlockedNotice from '../../alerts/AlertBlockedNotice';

/** The tints the banner can wear — one per work-item category, named for the palette rather than the category. */
export type RecordAlertBannerTone = 'warning' | 'blue-light' | 'error';

interface IToneStyle {
  surface: string;
  hover: string;
  Icon: React.ComponentType<{ className?: string }>;
  glyph: string;
}

/**
 * Figma tints the icon and chevron with the palette's 500 step — the same
 * step the pill's border sits one shade below — rather than the 600 the
 * larger alert tiles use.
 */
const STYLE_BY_TONE: Record<RecordAlertBannerTone, IToneStyle> = {
  warning: {
    surface: 'bg-utility-warning-50 border-utility-warning-100',
    hover: 'hover:bg-utility-warning-100',
    Icon: AlertCircleIcon,
    glyph: 'text-utility-warning-500',
  },
  'blue-light': {
    surface: 'bg-utility-blue-light-50 border-utility-blue-light-100',
    hover: 'hover:bg-utility-blue-light-100',
    Icon: BellIcon,
    glyph: 'text-utility-blue-light-500',
  },
  error: {
    surface: 'bg-utility-error-50 border-utility-error-100',
    hover: 'hover:bg-utility-error-100',
    Icon: AlertCircleIcon,
    glyph: 'text-utility-error-500',
  },
};

interface IRecordAlertBannerBaseProps {
  /** The work item's title, truncated to one line. */
  title: string;
  tone: RecordAlertBannerTone;
  /** Expands the alert. Absent, the pill renders inert with no chevron. */
  onClick?: () => void;
  /**
   * Refusal feedback for a click the surface would not act on (the assistant
   * is mid-turn): dims the pill to 30% and floats a lock pill over it.
   */
  showBlockedNotice?: boolean;
  className?: string;
}

/**
 * The pill-shaped banner at the top of a record page's body (Figma
 * "Notification"): a tinted glyph, the alert's title, and a chevron promising
 * the thread it expands into. Purely presentational — the wrapper maps a work
 * item onto `title` / `tone` and decides whether there is anything to open.
 *
 * Renders a button when `onClick` is given and a plain div otherwise; the
 * chevron goes with the button, since on an inert pill it would promise an
 * expansion that never comes.
 */
const RecordAlertBannerBase = ({
  title,
  tone,
  onClick,
  showBlockedNotice = false,
  className,
}: IRecordAlertBannerBaseProps) => {
  const { surface, hover, Icon, glyph } = STYLE_BY_TONE[tone];

  const pillClass = cx(
    'flex h-12 w-full items-center justify-center gap-2 rounded-2xl border px-3 py-2 shadow-xs',
    'transition-opacity duration-200',
    surface,
    showBlockedNotice && 'opacity-30'
  );

  const body = (
    <>
      <Icon aria-hidden="true" className={cx('size-[18px] shrink-0', glyph)} />
      <span className="text-primary text-md min-w-0 truncate font-semibold">{title}</span>
    </>
  );

  return (
    // The lock pill hangs off this wrapper rather than the pill itself, so it
    // stays at full opacity while the pill dims.
    <div className={cx('relative w-full', className)}>
      {onClick ? (
        <button
          type="button"
          onClick={onClick}
          className={cx(
            pillClass,
            hover,
            'outline-focus-ring cursor-pointer transition-colors focus-visible:outline-2 focus-visible:outline-offset-1'
          )}
        >
          {body}
          <ChevronDownIcon aria-hidden="true" className={cx('size-5 shrink-0', glyph)} />
        </button>
      ) : (
        <div className={pillClass}>{body}</div>
      )}
      <AlertBlockedNotice show={showBlockedNotice} />
    </div>
  );
};

export default RecordAlertBannerBase;
